import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePanelsStore } from "../stores/usePanelsStore";
import { getElevationProfile } from "../utils/elevationMap.js";
import { heartRateContinuityGapMs, heartRateLinkGapMs } from "../utils/heartRateChart.js";
import { useTranslate } from "../i18n/useTranslate";
import {
  calculateTrackElevationStats,
  sanitizeElevationPoints,
} from "../utils/elevationStats.js";

const elevationProfileCache = new Map();
const ELEVATION_PROFILE_STORAGE_KEY = "trekkin.elevationProfiles.v2";
const ELEVATION_PROFILE_STORAGE_LIMIT = 24;
const FROZEN_ELEVATION_TOLERANCE_M = 0.75;

function readPointElevation(point) {
  const val = Number(point?.ele ?? point?.elevation ?? point?.alt);
  return Number.isFinite(val) ? val : null;
}

function pointsMatch(a, b) {
  const aTs = Number(a?.ts ?? a?.time);
  const bTs = Number(b?.ts ?? b?.time);
  if (Number.isFinite(aTs) && aTs > 0 && Number.isFinite(bTs) && bTs > 0) {
    return aTs === bTs;
  }
  const aLat = Number(a?.lat);
  const aLon = Number(a?.lon ?? a?.lng);
  const bLat = Number(b?.lat);
  const bLon = Number(b?.lon ?? b?.lng);
  return (
    [aLat, aLon, bLat, bLon].every(Number.isFinite) &&
    Math.abs(aLat - bLat) < 1e-7 &&
    Math.abs(aLon - bLon) < 1e-7
  );
}

function extendLiveElevationProfile(profile, points) {
  if (
    !Array.isArray(profile) ||
    !Array.isArray(points) ||
    profile.length < 2 ||
    profile.length >= points.length ||
    !pointsMatch(profile[0], points[0]) ||
    !pointsMatch(profile[profile.length - 1], points[profile.length - 1])
  ) {
    return null;
  }

  const profileAnchor = readPointElevation(profile[profile.length - 1]);
  if (!Number.isFinite(profileAnchor)) return null;
  const sourceAnchor = readPointElevation(points[profile.length - 1]);

  return points.map((point, index) => {
    if (index < profile.length) {
      const ele = readPointElevation(profile[index]);
      return Number.isFinite(ele) ? { ...point, ele } : point;
    }
    const sourceElevation = readPointElevation(point);
    const relativeDelta =
      Number.isFinite(sourceAnchor) && Number.isFinite(sourceElevation)
        ? Math.max(-20, Math.min(20, sourceElevation - sourceAnchor))
        : 0;
    return { ...point, ele: profileAnchor + relativeDelta };
  });
}

function distanceMeters(a, b) {
  const lat1 = Number(a?.lat);
  const lon1 = Number(a?.lon ?? a?.lng);
  const lat2 = Number(b?.lat);
  const lon2 = Number(b?.lon ?? b?.lng);
  if (
    !Number.isFinite(lat1) ||
    !Number.isFinite(lon1) ||
    !Number.isFinite(lat2) ||
    !Number.isFinite(lon2)
  ) {
    return 0;
  }
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const la1 = toRad(lat1);
  const la2 = toRad(lat2);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function isAndroidElevationSource(point) {
  const src = String(point?.src || "");
  return (
    point?.fromBuffer === true ||
    src === "android-bg" ||
    src === "android-buffer" ||
    src === "android-fg"
  );
}

function isAndroidRuntime() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(String(navigator.userAgent || ""));
}

function findFrozenElevationRanges(points, elevations) {
  const ranges = [];
  if (!Array.isArray(points) || !Array.isArray(elevations)) return ranges;
  const androidRuntime = isAndroidRuntime();

  for (let i = 0; i < elevations.length; i += 1) {
    const base = elevations[i];
    if (!Number.isFinite(base)) continue;

    let end = i;
    let hasAndroidSource = androidRuntime || isAndroidElevationSource(points[i]);
    while (
      end + 1 < elevations.length &&
      Number.isFinite(elevations[end + 1]) &&
      Math.abs(elevations[end + 1] - base) <= FROZEN_ELEVATION_TOLERANCE_M
    ) {
      end += 1;
      if (isAndroidElevationSource(points[end])) hasAndroidSource = true;
    }

    const count = end - i + 1;
    if (count >= 20) {
      const first = points[i];
      const last = points[end];
      const durationMs = Math.max(
        0,
        Number(last?.ts ?? last?.time ?? 0) - Number(first?.ts ?? first?.time ?? 0)
      );
      const movedM = distanceMeters(first, last);
      const hasMovement = movedM >= 100 && durationMs >= 60000;

      if (hasAndroidSource && hasMovement) {
        ranges.push([i, end]);
      }
    }

    i = end;
  }

  return ranges;
}

function interpolateMissingElevations(values) {
  const out = [...values];
  let firstValid = -1;
  for (let i = 0; i < out.length; i += 1) {
    if (Number.isFinite(out[i])) {
      firstValid = i;
      break;
    }
  }
  if (firstValid < 0) return [];

  for (let i = 0; i < firstValid; i += 1) out[i] = out[firstValid];

  let lastValid = firstValid;
  for (let i = firstValid + 1; i < out.length; i += 1) {
    if (Number.isFinite(out[i])) {
      if (i - lastValid > 1) {
        const start = Number(out[lastValid]);
        const end = Number(out[i]);
        const span = i - lastValid;
        for (let j = lastValid + 1; j < i; j += 1) {
          const t = (j - lastValid) / span;
          out[j] = start + (end - start) * t;
        }
      }
      lastValid = i;
    }
  }

  for (let i = lastValid + 1; i < out.length; i += 1) out[i] = out[lastValid];
  return out;
}

function hasFrozenElevationRuns(points) {
  if (!Array.isArray(points) || points.length < 8) return false;
  const elevations = points.map(readPointElevation);
  return findFrozenElevationRanges(points, elevations).length > 0;
}

function readStoredElevationProfile(pointsKey, points) {
  if (!pointsKey || !Array.isArray(points) || points.length < 2) return null;
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(ELEVATION_PROFILE_STORAGE_KEY) || "{}"
    );
    const elevations = stored?.[pointsKey]?.elevations;
    if (!Array.isArray(elevations) || elevations.length !== points.length) {
      return null;
    }
    const restored = points.map((point, index) => {
      const ele = Number(elevations[index]);
      return Number.isFinite(ele) ? { ...point, ele } : point;
    });
    return restored.some((point) => Number.isFinite(Number(point?.ele)))
      ? restored
      : null;
  } catch {
    return null;
  }
}

function storeElevationProfile(pointsKey, points) {
  if (!pointsKey || !Array.isArray(points) || points.length < 2) return;
  const elevations = points.map((point) => {
    const ele = Number(point?.ele ?? point?.elevation ?? point?.alt);
    return Number.isFinite(ele) ? Math.round(ele) : null;
  });
  if (!elevations.some((ele) => Number.isFinite(ele))) return;
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(ELEVATION_PROFILE_STORAGE_KEY) || "{}"
    );
    stored[pointsKey] = { elevations, savedAt: Date.now() };
    const trimmed = Object.fromEntries(
      Object.entries(stored)
        .sort(
          ([, a], [, b]) => Number(b?.savedAt || 0) - Number(a?.savedAt || 0)
        )
        .slice(0, ELEVATION_PROFILE_STORAGE_LIMIT)
    );
    window.localStorage.setItem(
      ELEVATION_PROFILE_STORAGE_KEY,
      JSON.stringify(trimmed)
    );
  } catch {}
}

export default function TrekkinChart({
  height = 120,
  points: externalPoints,
  useElevationMap = false,
  forceElevationMap = false,
  liveElevationProfile = false,
  showStats = false,
  showProgressOverlay = false,
  progressPoint = null,
  progressColor = "#1f78ff",
  progressLineWidth = 2.8,
  background = "rgba(255,255,255,0.9)",
  emptyBackground = "rgba(255,255,255,0.85)",
  emptyColor = "#777",
  lineColor = "#28a745",
  lineWidth = 2,
  lineOutlineColor = null,
  lineOutlineWidth = 0,
  showElevationLine = true,
  showElevationGuides = true,
  showHeartRateLine = true,
  onElevationStatsChange = null,
  heartRateSamples = null,
  padding = 4,
  borderRadius = 10,
  fillColor = null,
  showGrid = false,
  showDistanceAxis = false,
}) {
  const translate = useTranslate();
  const storePoints = usePanelsStore((s) => s.points);
  const isRecording = usePanelsStore((s) => !!s.isRecording);
  const points =
    externalPoints && externalPoints.length > 1
      ? externalPoints
      : storePoints;
  const usesStorePoints = !externalPoints || externalPoints === storePoints;
  const isLiveElevationProfile =
    liveElevationProfile || (!externalPoints && isRecording && usesStorePoints);
  const [elevatedPointsState, setElevatedPointsState] = useState({
    key: "",
    points: null,
  });
  const [elevationLoading, setElevationLoading] = useState(false);
  const lastElevationRequestRef = useRef({ ts: 0, count: 0 });
  const pointsKey = useMemo(() => {
    if (!Array.isArray(points) || points.length < 2) return "";
    const first = points[0] || {};
    const middle = points[Math.floor(points.length / 2)] || {};
    const last = points[points.length - 1] || {};
    return [
      points.length,
      Number(first?.ts ?? first?.time ?? 0),
      Number(first?.lat ?? 0).toFixed(6),
      Number(first?.lon ?? first?.lng ?? 0).toFixed(6),
      Number(middle?.lat ?? 0).toFixed(6),
      Number(middle?.lon ?? middle?.lng ?? 0).toFixed(6),
      Number(last?.ts ?? last?.time ?? 0),
      Number(last?.lat ?? 0).toFixed(6),
      Number(last?.lon ?? last?.lng ?? 0).toFixed(6),
    ].join("|");
  }, [points]);

  const elevatedPoints = useMemo(() => {
    const cached = elevatedPointsState?.points;
    if (
      elevatedPointsState?.key === pointsKey &&
      Array.isArray(cached) &&
      Array.isArray(points) &&
      cached.length === points.length
    ) {
      return cached;
    }
    if (isLiveElevationProfile) {
      return extendLiveElevationProfile(cached, points);
    }
    return null;
  }, [elevatedPointsState, isLiveElevationProfile, points, pointsKey]);

  const getNormalizedElevations = (list) => {
    if (!Array.isArray(list) || !list.length) return [];
    const plausiblePoints = sanitizeElevationPoints(list, {
      maxDelta: 30,
      maxVerticalSpeed: 0.7,
      minVerticalAllowance: 3,
    });
    const raw = plausiblePoints.map(readPointElevation);

    // Remove picos isolados típicos de altitude inválida (ex.: queda abrupta para 0)
    const cleaned = [...raw];
    for (let i = 1; i < cleaned.length - 1; i += 1) {
      const prev = cleaned[i - 1];
      const cur = cleaned[i];
      const next = cleaned[i + 1];
      if (!Number.isFinite(prev) || !Number.isFinite(cur) || !Number.isFinite(next)) continue;
      const d1 = cur - prev;
      const d2 = next - cur;
      const oppositeJumps = Math.abs(d1) > 40 && Math.abs(d2) > 40 && d1 * d2 < 0;
      const suspiciousLow =
        cur >= 0 &&
        cur <= 5 &&
        Math.abs(prev) > 20 &&
        Math.abs(next) > 20 &&
        Math.abs(prev - next) < 30;
      if (oppositeJumps || suspiciousLow) cleaned[i] = null;
    }

    // Remove blocos artificiais a 0-5 m causados por fixes Android sem altitude.
    // Uma aproximação real ao nível do mar é gradual, não um salto vertical.
    for (let i = 0; i < cleaned.length; i += 1) {
      if (!Number.isFinite(cleaned[i]) || cleaned[i] < 0 || cleaned[i] > 5) continue;
      const runStart = i;
      while (
        i + 1 < cleaned.length &&
        Number.isFinite(cleaned[i + 1]) &&
        cleaned[i + 1] >= 0 &&
        cleaned[i + 1] <= 5
      ) {
        i += 1;
      }
      const runEnd = i;
      const before = runStart > 0 ? cleaned[runStart - 1] : null;
      const after = runEnd + 1 < cleaned.length ? cleaned[runEnd + 1] : null;
      const abruptBefore = Number.isFinite(before) && before > 40;
      const abruptAfter = Number.isFinite(after) && after > 40;
      if (abruptBefore || abruptAfter) {
        for (let j = runStart; j <= runEnd; j += 1) cleaned[j] = null;
      }
    }

    // Android pode devolver altitude congelada ao milímetro durante dezenas de
    // pontos. Isso cria patamares falsos no perfil de elevação.
    for (const [start, end] of findFrozenElevationRanges(plausiblePoints, cleaned)) {
      for (let i = start; i <= end; i += 1) cleaned[i] = null;
    }

    // backfill para pontos iniciais sem elevação
    let firstValid = -1;
    for (let i = 0; i < cleaned.length; i += 1) {
      if (cleaned[i] != null) {
        firstValid = i;
        break;
      }
    }
    if (firstValid > 0) {
      for (let i = 0; i < firstValid; i += 1) cleaned[i] = cleaned[firstValid];
    }

    // Sem qualquer elevação real, não desenhar um perfil plano falso.
    if (firstValid < 0) return [];
    return interpolateMissingElevations(cleaned);
  };

  const needsElevation = useMemo(() => {
    if (!useElevationMap) return false;
    if (!Array.isArray(points) || points.length < 2) return false;
    if (forceElevationMap) return true;
    const androidLikeCount = points.reduce(
      (count, point) => count + (isAndroidElevationSource(point) ? 1 : 0),
      0
    );
    if (
      isLiveElevationProfile &&
      (isAndroidRuntime() || androidLikeCount >= Math.max(2, points.length * 0.25))
    ) {
      return true;
    }
    const hasAny = points.some(
      (p) => Number.isFinite(p.ele) || Number.isFinite(p.elevation)
    );
    if (!hasAny) return true;
    const allZero = points.every((p) => {
      const val = Number(p.ele ?? p.elevation ?? 0);
      return !Number.isFinite(val) || val === 0;
    });
    return allZero || hasFrozenElevationRuns(points);
  }, [forceElevationMap, isLiveElevationProfile, points, useElevationMap]);

  useEffect(() => {
    if (!needsElevation) {
      setElevatedPointsState({ key: "", points: null });
      setElevationLoading(false);
      return;
    }
    let cancelled = false;
    const memoryCached =
      pointsKey && elevationProfileCache.has(pointsKey)
        ? elevationProfileCache.get(pointsKey)
        : null;
    const cached =
      Array.isArray(memoryCached) && memoryCached.length > 1
        ? memoryCached
        : readStoredElevationProfile(pointsKey, points);
    if (Array.isArray(cached) && cached.length > 1) {
      elevationProfileCache.set(pointsKey, cached);
      setElevatedPointsState({ key: pointsKey, points: cached });
      setElevationLoading(false);
      return;
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      if (!isLiveElevationProfile) {
        setElevatedPointsState({ key: "", points: null });
      }
      setElevationLoading(false);
      return;
    }

    const run = async () => {
      try {
        const androidLiveRecording =
          isAndroidRuntime() &&
          isRecording &&
          isLiveElevationProfile;
        if (androidLiveRecording) {
          const last = lastElevationRequestRef.current || {};
          const now = Date.now();
          const countDelta = Math.abs(points.length - Number(last.count || 0));
          if (last.ts && now - last.ts < 30000 && countDelta < 40) {
            setElevationLoading(false);
            return;
          }
          lastElevationRequestRef.current = { ts: now, count: points.length };
        }
        setElevationLoading(true);
        const nextPoints = await getElevationProfile(
          points,
          androidLiveRecording
            ? { maxSamples: 120, targetSpacingMeters: 120 }
            : undefined
        );
        if (!cancelled) {
          const normalized = Array.isArray(nextPoints) ? nextPoints : null;
          setElevatedPointsState({ key: pointsKey, points: normalized });
          if (pointsKey && Array.isArray(normalized) && normalized.length > 1) {
            elevationProfileCache.set(pointsKey, normalized);
            storeElevationProfile(pointsKey, normalized);
          }
          setElevationLoading(false);
        }
      } catch {
        if (!cancelled) {
          if (!isLiveElevationProfile) {
            setElevatedPointsState({ key: "", points: null });
          }
          setElevationLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    externalPoints,
    isLiveElevationProfile,
    isRecording,
    needsElevation,
    points,
    pointsKey,
    storePoints,
  ]);

  const chartPoints = elevatedPoints || points;
  const normalizedElevations = useMemo(
    () => getNormalizedElevations(chartPoints),
    [chartPoints]
  );
  const SVG_W = 1000; // largura virtual grande → escala perfeita
  const SVG_H = height - 20;
  const progressTarget = useMemo(() => {
    const lat = Number(progressPoint?.lat);
    const lon = Number(progressPoint?.lon ?? progressPoint?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon };
    return null;
  }, [progressPoint]);

  const stats = useMemo(() => {
    if (!Array.isArray(chartPoints) || chartPoints.length < 2) {
      return { distanceKm: null, ascent: null, descent: null };
    }

    const hasDist = chartPoints.every(
      (p) => typeof p.dist === "number" || typeof p.distance === "number"
    );
    let distanceKm = null;

    if (hasDist) {
      const distValues = chartPoints
        .map((p) => Number(p.dist ?? p.distance))
        .filter((v) => Number.isFinite(v));
      if (distValues.length) {
        const minDist = Math.min(...distValues);
        const maxDist = Math.max(...distValues);
        const spanDist = Math.max(0, maxDist - minDist);
        distanceKm = spanDist / 1000;
      }
    } else if (
      Number.isFinite(chartPoints[0]?.lat) &&
      Number.isFinite(chartPoints[0]?.lon)
    ) {
      let total = 0;
      for (let i = 1; i < chartPoints.length; i++) {
        const a = chartPoints[i - 1];
        const b = chartPoints[i];
        const R = 6371000;
        const toRad = (d) => (d * Math.PI) / 180;
        const dLat = toRad(b.lat - a.lat);
        const dLon = toRad(b.lon - a.lon);
        const la1 = toRad(a.lat);
        const la2 = toRad(b.lat);
        const h =
          Math.sin(dLat / 2) ** 2 +
          Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
        total += 2 * R * Math.asin(Math.sqrt(h));
      }
      distanceKm = total / 1000;
    }

    const elevs = normalizedElevations;
    if (elevs.length < 2) {
      return { distanceKm, ascent: null, descent: null };
    }
    const normalizedPoints = elevs.map((ele) => ({ ele }));
    const { gain: ascent, loss: descent } = calculateTrackElevationStats(normalizedPoints);

    return {
      distanceKm,
      ascent: Math.round(ascent),
      descent: Math.round(descent),
    };
  }, [chartPoints, normalizedElevations]);

  useEffect(() => {
    if (typeof onElevationStatsChange !== "function") return;
    onElevationStatsChange({
      gain: Number.isFinite(stats.ascent) ? stats.ascent : null,
      loss: Number.isFinite(stats.descent) ? stats.descent : null,
      valid: Number.isFinite(stats.ascent) && Number.isFinite(stats.descent),
    });
  }, [onElevationStatsChange, stats.ascent, stats.descent]);

  const { pathD, minEle, maxEle, progressPathD, progressMarker } = useMemo(() => {
    if (!Array.isArray(chartPoints) || chartPoints.length < 2) {
      return {
        pathD: "",
        minEle: 0,
        maxEle: 0,
        progressPathD: "",
        progressMarker: null,
      };
    }

    const elevs = normalizedElevations;
    if (elevs.length < 2) {
      return {
        pathD: "",
        minEle: 0,
        maxEle: 0,
        progressPathD: "",
        progressMarker: null,
      };
    }

    const min = Math.min(...elevs);
    const max = Math.max(...elevs);
    const span = max - min || 1;

    const hasDist = chartPoints.every(
      (p) =>
        typeof p.dist === "number" ||
        typeof p.distance === "number"
    );

    let xs = [];

    if (hasDist) {
      const distValues = chartPoints
        .map((p) => Number(p.dist ?? p.distance))
        .filter((v) => Number.isFinite(v));
      const minDist = distValues.length ? Math.min(...distValues) : 0;
      const maxDist = distValues.length ? Math.max(...distValues) : 1;
      const spanDist = Math.max(1, maxDist - minDist);
      const firstDist = Number(chartPoints[0]?.dist ?? chartPoints[0]?.distance);
      const lastDist = Number(
        chartPoints[chartPoints.length - 1]?.dist ??
          chartPoints[chartPoints.length - 1]?.distance
      );
      const isReversedDirection =
        Number.isFinite(firstDist) &&
        Number.isFinite(lastDist) &&
        firstDist > lastDist;

      xs = chartPoints.map((p) => {
        const d = Number(p.dist ?? p.distance ?? minDist);
        const baseX = (d - minDist) / spanDist;
        const x = isReversedDirection ? 1 - baseX : baseX;
        return Number.isFinite(x) ? Math.max(0, Math.min(1, x)) : 0;
      });
    } else {
      xs = chartPoints.map((_, i) => i / (chartPoints.length - 1));
    }

    const toY = (ele) => {
      const norm = (ele - min) / span;
      let y = SVG_H - norm * SVG_H;
      y = Math.max(0, Math.min(SVG_H, y));
      return y;
    };

    const buildPath = (endIndex = chartPoints.length - 1) => {
      if (endIndex < 1) return "";
      let d = "";
      for (let i = 0; i <= endIndex; i++) {
        const x = xs[i] * SVG_W;
        const y = toY(elevs[i]);
        d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
      }
      return d;
    };

    const basePath = buildPath(chartPoints.length - 1);

    let markerIndex = -1;
    if (showProgressOverlay && progressTarget) {
      let best = Number.POSITIVE_INFINITY;
      for (let i = 0; i < chartPoints.length; i++) {
        const p = chartPoints[i];
        const lat = Number(p?.lat);
        const lon = Number(p?.lon ?? p?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
        const dLat = lat - progressTarget.lat;
        const dLon = lon - progressTarget.lon;
        const score = dLat * dLat + dLon * dLon;
        if (score < best) {
          best = score;
          markerIndex = i;
        }
      }
    }

    let overlayPath = "";
    let overlayMarker = null;
    if (markerIndex > 0) {
      overlayPath = buildPath(markerIndex);
      overlayMarker = {
        x: xs[markerIndex] * SVG_W,
        y: toY(elevs[markerIndex]),
      };
    }

    return {
      pathD: basePath,
      minEle: min,
      maxEle: max,
      progressPathD: overlayPath,
      progressMarker: overlayMarker,
    };
  }, [
    chartPoints,
    normalizedElevations,
    progressTarget,
    showProgressOverlay,
    SVG_H,
  ]);

  const heartRateOverlay = useMemo(() => {
    const normalizeTime = (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric <= 0) return NaN;
      return numeric < 1e12 ? numeric * 1000 : numeric;
    };
    const samples = Array.isArray(heartRateSamples)
      ? heartRateSamples
          .map((sample) => ({
            bpm: Number(sample?.bpm),
            ts: normalizeTime(sample?.ts ?? sample?.time ?? sample?.timestamp),
            sparse: sample?.sparse === true,
          }))
          .filter((sample) =>
            Number.isFinite(sample.bpm) && sample.bpm >= 25 && sample.bpm <= 260
          )
      : [];
    if (samples.length < 2) return null;

    const values = samples.map((sample) => sample.bpm);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = Math.max(1, max - min);
    const pointTimes = (chartPoints || [])
      .map((point) => normalizeTime(point?.ts ?? point?.time ?? point?.timestamp))
      .filter(Number.isFinite);
    const validSampleTimes = samples.map((sample) => sample.ts).filter(Number.isFinite);
    const startTs = pointTimes.length
      ? pointTimes[0]
      : Math.min(...validSampleTimes);
    const endTs = pointTimes.length
      ? pointTimes[pointTimes.length - 1]
      : Math.max(...validSampleTimes);
    const timeSpan = Math.max(1, endTs - startTs);

    const coordinates = samples.map((sample, index) => {
      const fallbackX = index / Math.max(1, samples.length - 1);
      const timeX = Number.isFinite(sample.ts)
        ? (sample.ts - startTs) / timeSpan
        : fallbackX;
      const x = Math.max(0, Math.min(1, timeX)) * SVG_W;
      // With a constant BPM series, min === max and the old formula placed
      // the stroke exactly on the lower SVG edge, where it was clipped and
      // looked like no heart-rate graph had been drawn.
      const y = max === min
        ? SVG_H / 2
        : SVG_H - ((sample.bpm - min) / span) * SVG_H;
      return { x, y: Math.max(0, Math.min(SVG_H, y)), ts: sample.ts, sparse: sample.sparse };
    });
    // Um sensor pode deixar de enviar amostras no background e retomá-las mais
    // tarde. Ligar os extremos desse intervalo desenha uma descida/subida
    // diagonal que nunca aconteceu. Começar um novo subcaminho sempre que a
    // lacuna exceder claramente a cadência normal do sensor.
    const maxContinuousGapMs = heartRateContinuityGapMs(samples.map((sample) => sample.ts));
    const path = coordinates
      .map(({ x, y, ts, sparse }, index) => {
        const previous = coordinates[index - 1];
        const previousTs = previous?.ts;
        const gapMs = ts - previousTs;
        const allowedGapMs = heartRateLinkGapMs(previous, { sparse }, maxContinuousGapMs);
        const startsNewRun =
          index === 0 ||
          !Number.isFinite(ts) ||
          !Number.isFinite(previousTs) ||
          gapMs <= 0 ||
          gapMs > allowedGapMs;
        return `${startsNewRun ? "M" : "L"} ${x} ${y}`;
      })
      .join(" ");
    return { path, min, max };
  }, [heartRateSamples, chartPoints, SVG_H]);

  if (!pathD) {
    return (
      <div
        style={{
          height,
          borderRadius,
          background: emptyBackground,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          color: emptyColor,
        }}
      >
        {elevationLoading
          ? translate("chart.elevation.loading")
          : translate("chart.elevation.noData")}
      </div>
    );
  }

  return (
    <div
      className="tk-elevation-chart"
      style={{
        width: "100%",
        borderRadius,
        background,
        padding,
        // O eixo de distância fica abaixo da área SVG. Reserva esse espaço
        // para que a legenda/ações seguintes nunca se sobreponham ao gráfico.
        paddingBottom: showDistanceAxis ? padding + 16 : padding,
        boxSizing: "border-box",
      }}
    >
      <div style={{ position: "relative", height: SVG_H }}>
        <svg
          width="100%"
          height={SVG_H}
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          preserveAspectRatio="none"
        >
        {showGrid ? [0.25, 0.5, 0.75].map((ratio) => (
          <line key={ratio} x1="0" y1={SVG_H * ratio} x2={SVG_W} y2={SVG_H * ratio} stroke="rgba(31,41,55,0.12)" strokeWidth="1" />
        )) : null}
        {showElevationLine && showElevationGuides ? (
          <>
            <line
              x1="0"
              y1="2"
              x2={SVG_W}
              y2="2"
              stroke="rgba(31,41,55,0.32)"
              strokeWidth="1.5"
              strokeDasharray="12 10"
            />
            <line
              x1="0"
              y1={Math.max(0, SVG_H - 2)}
              x2={SVG_W}
              y2={Math.max(0, SVG_H - 2)}
              stroke="rgba(31,41,55,0.32)"
              strokeWidth="1.5"
              strokeDasharray="12 10"
            />
          </>
        ) : null}
        {showElevationLine && lineOutlineColor && lineOutlineWidth > lineWidth ? (
          <path
            d={pathD}
            fill="none"
            stroke={lineOutlineColor}
            strokeWidth={lineOutlineWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
        {showElevationLine && fillColor ? (
          <path d={`${pathD} L ${SVG_W} ${SVG_H} L 0 ${SVG_H} Z`} fill={fillColor} stroke="none" />
        ) : null}
        {showElevationLine ? (
          <path
            d={pathD}
            fill="none"
            stroke={lineColor}
            strokeWidth={lineWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
        {showHeartRateLine && heartRateOverlay?.path ? (
          <path
            d={heartRateOverlay.path}
            fill="none"
            stroke="#dc2626"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
        {showElevationLine && progressPathD ? (
          <path
            d={progressPathD}
            fill="none"
            stroke={progressColor}
            strokeWidth={progressLineWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
        {showElevationLine && progressMarker ? (
          <circle
            cx={progressMarker.x}
            cy={progressMarker.y}
            r="6"
            fill={progressColor}
            stroke="#fff"
            strokeWidth="2"
          />
        ) : null}
        </svg>
        {showDistanceAxis && Number.isFinite(stats.distanceKm) ? (
          <div className="tk-chart-distance-axis" aria-hidden="true">
            <span>0</span><span>{(stats.distanceKm / 2).toFixed(1)}</span><span>{stats.distanceKm.toFixed(1)} km</span>
          </div>
        ) : null}
        {showElevationLine && showElevationGuides ? (
          <>
            <span
              className="tk-chart-guide-label"
              style={{
                position: "absolute",
                top: 4,
                right: 6,
                borderRadius: 5,
                background: "rgba(255,255,255,0.82)",
                padding: "1px 4px",
                fontSize: 10,
                lineHeight: 1.2,
                fontWeight: 800,
                color: "#374151",
                pointerEvents: "none",
              }}
            >
              Máx. {Math.round(maxEle)} m
            </span>
            <span
              className="tk-chart-guide-label"
              style={{
                position: "absolute",
                bottom: 4,
                right: 6,
                borderRadius: 5,
                background: "rgba(255,255,255,0.82)",
                padding: "1px 4px",
                fontSize: 10,
                lineHeight: 1.2,
                fontWeight: 800,
                color: "#374151",
                pointerEvents: "none",
              }}
            >
              Mín. {Math.round(minEle)} m
            </span>
          </>
        ) : null}
        {showHeartRateLine && heartRateOverlay ? (
          <>
            <span
              className="tk-chart-guide-label tk-chart-heart-label"
              style={{
                position: "absolute",
                top: 4,
                left: 6,
                borderRadius: 5,
                background: "rgba(255,255,255,0.86)",
                padding: "1px 4px",
                fontSize: 10,
                lineHeight: 1.2,
                fontWeight: 800,
                color: "#dc2626",
                pointerEvents: "none",
              }}
            >
              Máx. {Math.round(heartRateOverlay.max)} BPM
            </span>
            <span
              className="tk-chart-guide-label tk-chart-heart-label"
              style={{
                position: "absolute",
                bottom: 4,
                left: 6,
                borderRadius: 5,
                background: "rgba(255,255,255,0.86)",
                padding: "1px 4px",
                fontSize: 10,
                lineHeight: 1.2,
                fontWeight: 800,
                color: "#dc2626",
                pointerEvents: "none",
              }}
            >
              Mín. {Math.round(heartRateOverlay.min)} BPM
            </span>
          </>
        ) : null}
      </div>
      {showStats && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 10,
            fontSize: 12,
            color: "#555",
            marginTop: showDistanceAxis ? 24 : 6,
          }}
        >
          <div>
            {translate("chart.stats.distance")}{" "}
            {typeof stats.distanceKm === "number"
              ? `${stats.distanceKm.toFixed(2)} km`
              : "—"}
          </div>
          <div>
            {translate("chart.stats.ascent")}{" "}
            {typeof stats.ascent === "number" ? `${stats.ascent} m` : "—"}
          </div>
          <div>
            {translate("chart.stats.descent")}{" "}
            {typeof stats.descent === "number" ? `${stats.descent} m` : "—"}
          </div>
        </div>
      )}
    </div>
  );
}
